'use client'

import MultiSelectFilter from '@/components/common/MultiSelectFilter'
import { GlobalOffer, Offer } from '@/payload-types'
import { Language } from '@/types/language.types'
import { useTranslations } from 'next-intl'
import React from 'react'
import { queryOffers, queryOfferFilterOptions, type OfferFilterOption } from './action'
import { transformToListCards } from '@/lib/helpers/transformToListCard'
import ListCard from '@/components/common/ListCard'
import { Button } from '@/components/ui/button'
import CardCarousel from '@/components/common/CardCarousel'

interface OffersClientProps {
  initialItems: Offer[]
  locale: Language
  cities: {
    title: string
    slug?: string | null
    countrySlug?: string | null
    localizedCountrySlug?: string | null
  }[]
  countries: { title: string; slug?: string | null }[]
  categories: { title: string; slug?: string | null }[]
  filterOptions: OfferFilterOption
  globalOffers: Pick<GlobalOffer, 'id' | 'title' | 'slug' | 'description' | 'image' | 'promo'>[]
  countryQuery: string | undefined
  cityQuery: string | undefined
  categoryQuery: string | undefined
}

function OffersClient({
  initialItems,
  locale,
  cities,
  countries,
  categories,
  filterOptions: initialFilterOptions,
  globalOffers,
  countryQuery,
  cityQuery,
  categoryQuery,
}: OffersClientProps) {
  const [filterCities, setFilterCities] = React.useState<string[]>(cityQuery ? [cityQuery] : [])
  const [filterCountries, setFilterCountries] = React.useState<string[]>(
    countryQuery ? [countryQuery] : [],
  )
  const [filterCategories, setFilterCategories] = React.useState<string[]>(
    categoryQuery ? [categoryQuery] : [],
  )

  const [availableOptions, setAvailableOptions] =
    React.useState<OfferFilterOption>(initialFilterOptions)

  const [items, setItems] = React.useState<Offer[]>(initialItems)
  const [page, setPage] = React.useState<number>(1)
  const [hasMore, setHasMore] = React.useState<boolean>(cityQuery ? false : true)
  const [loading, setLoading] = React.useState<boolean>(false)

  const t = useTranslations()

  const cityOptions = React.useMemo(() => {
    if (filterCountries.length === 0) return cities
    return cities.filter((city) =>
      filterCountries.includes(city.localizedCountrySlug ?? city.countrySlug ?? ''),
    )
  }, [cities, filterCountries])

  const updateFilters = async (
    newCountries: string[],
    newCities: string[],
    newCategories: string[],
  ) => {
    setLoading(true)
    setItems([])
    setPage(1)
    setHasMore(true)

    const [result, options] = await Promise.all([
      queryOffers(
        locale,
        1,
        9,
        newCities.length > 0 ? newCities : undefined,
        newCountries.length > 0 ? newCountries : undefined,
        newCategories.length > 0 ? newCategories : undefined,
      ),
      queryOfferFilterOptions(
        locale,
        newCountries.length > 0 ? newCountries : undefined,
        newCities.length > 0 ? newCities : undefined,
        newCategories.length > 0 ? newCategories : undefined,
      ),
    ])

    setItems(result.docs)
    setHasMore(result.hasNextPage)
    setAvailableOptions(options)
    setLoading(false)
  }

  const loadMore = async () => {
    setLoading(true)
    const nextPage = page + 1
    const result = await queryOffers(
      locale,
      nextPage,
      9,
      filterCities.length > 0 ? filterCities : undefined,
      filterCountries.length > 0 ? filterCountries : undefined,
      filterCategories.length > 0 ? filterCategories : undefined,
    )

    setItems((prev) => [...prev, ...result.docs])
    setPage(nextPage)
    setHasMore(result.hasNextPage)
    setLoading(false)
  }

  const onFilterCountriesChange = (vals: string[]) => {
    setFilterCountries(vals)
    const newCityFilter =
      vals.length === 0
        ? filterCities
        : filterCities.filter((citySlug) => {
            const city = cities.find((c) => c.slug === citySlug)
            return city && vals.includes(city.localizedCountrySlug ?? city.countrySlug ?? '')
          })
    setFilterCities(newCityFilter)
    updateFilters(vals, newCityFilter, filterCategories)
  }

  const onFilterCitiesChange = (vals: string[]) => {
    setFilterCities(vals)
    updateFilters(filterCountries, vals, filterCategories)
  }

  const onFilterCategoriesChange = (vals: string[]) => {
    setFilterCategories(vals)
    updateFilters(filterCountries, filterCities, vals)
  }

  const offers = transformToListCards(items, `/offers/hotel`)
  const globalOfferCards = transformToListCards(globalOffers, `/offers`)

  return (
    <div className='space-y-4'>
      <div className='space-y-4 pb-8'>
        <h1>{t('our_best_offers')}</h1>
        <CardCarousel items={globalOfferCards} />
      </div>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <h2 className='text-3xl'>{t('offers')}</h2>
        <div className='flex gap-2'>
          <MultiSelectFilter
            values={filterCountries}
            items={countries
              .filter((country) => availableOptions.countrySlugs.includes(country.slug ?? ''))
              .map((country) => ({
                title: country.title,
                value: country?.slug ?? '',
              }))}
            placeholder={t('country')}
            onValuesChange={onFilterCountriesChange}
          />
          <MultiSelectFilter
            values={filterCities}
            items={cityOptions
              .filter((city) => availableOptions.citySlugs.includes(city.slug ?? ''))
              .map((city) => ({
                title: city.title,
                value: city?.slug ?? '',
              }))}
            onValuesChange={onFilterCitiesChange}
            placeholder={t('city')}
          />
          {categories.length > 0 && (
            <MultiSelectFilter
              values={filterCategories}
              items={categories
                .filter((category) => availableOptions.categorySlugs.includes(category.slug ?? ''))
                .map((category) => ({
                  title: category.title,
                  value: category?.slug ?? '',
                }))}
              onValuesChange={onFilterCategoriesChange}
              placeholder={t('category')}
            />
          )}
        </div>
      </div>
      <div className='grid gap-8 sm:grid-cols-2 lg:grid-cols-3'>
        {loading && items.length === 0
          ? Array.from({ length: 6 }).map((_, i) => <OfferSkeleton key={i} />)
          : offers.map((item) => (
              <ListCard key={item.title} {...item} className='mb-4 w-full' />
            ))}
      </div>
      {hasMore && (
        <div className='flex w-full justify-center'>
          <Button variant='outline' onClick={loadMore} disabled={loading}>
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  )
}

function OfferSkeleton() {
  return (
    <div className='mb-4 w-full'>
      <div className='relative aspect-[16/9] w-full max-w-2xl animate-pulse overflow-hidden rounded-lg bg-stone-200' />
      <div className='space-y-2 pt-2'>
        <div className='h-6 w-3/4 animate-pulse rounded bg-stone-200' />
        <div className='h-4 w-full animate-pulse rounded bg-stone-200' />
        <div className='h-4 w-5/6 animate-pulse rounded bg-stone-200' />
      </div>
    </div>
  )
}

export default OffersClient
